<template>
    <div class="relative flex-1">
        <div class="bg-white border-b border-gray-200 py-6 sm:px-6 lg:px-8 px-5">
            <div class="md:flex md:items-center md:justify-between">
                <div class="flex-1 min-w-0">
                    <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Customers') }}</h1>
                </div>
                <div class="mt-4 flex md:mt-0 md:ml-4">
                    <router-link
                        class="btn btn-blue shadow-sm rounded-md mr-4"
                        to="/dashboard/admin/customers/new"
                    >
                        {{ $t('Create customer') }}
                    </router-link>
                    <div class="rounded-md shadow-sm mr-4">
                        <button
                            id="filter-button"
                            class="inline-flex justify-center w-full rounded-md border border-gray-400 px-4 py-2 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150"
                            type="button"
                            @click="openFiltersSidebar"
                        >
                            <svg-vue class="self-center mr-3 h-4 w-4" icon="font-awesome.filter-regular"></svg-vue>
                            {{ $t('Filters') }}
                            <svg-vue class="self-center ml-2.5 -mr-1.5 h-3 w-3" icon="font-awesome.chevron-right-solid"></svg-vue>
                        </button>
                        <div v-show="filtersSidebar" class="fixed inset-0 overflow-hidden z-30">
                            <div class="absolute inset-0 overflow-hidden">
                                <section v-on-clickaway="closeFiltersSidebar" :style="{top: '65px'}" class="absolute inset-y-0 right-0 pl-10 max-w-full flex">
                                    <transition
                                        duration="500"
                                        enter-active-class="transform transition ease-in-out duration-500"
                                        enter-class="translate-x-full"
                                        enter-to-class="translate-x-0"
                                        leave-active-class="transform transition ease-in-out duration-500"
                                        leave-class="translate-x-0"
                                        leave-to-class="translate-x-full"
                                    >
                                        <div v-show="filtersSidebar" class="w-screen max-w-xs">
                                            <div class="h-full flex flex-col space-y-4 bg-white shadow-xl">
                                                <header class="px-4 pt-4 sm:px-6">
                                                    <div class="flex items-start justify-between space-x-3">
                                                        <h2 class="text-lg leading-7 font-medium text-gray-900">
                                                            {{ $t('Filters') }}
                                                        </h2>
                                                        <div class="h-7 flex items-center">
                                                            <button aria-label="Close panel" class="text-gray-400 hover:text-gray-500 transition ease-in-out duration-150" type="button" @click="closeFiltersSidebar">
                                                                <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </header>
                                                <div class="relative flex-1 px-4 sm:px-6 h-full border-t overflow-y-scroll">
                                                    <div class="absolute inset-0 px-4 sm:px-6">
                                                        <div class="grid grid-cols-3 py-3">
                                                            <div class="col-span-3 mb-2">
                                                                <label class="block text-sm font-medium leading-5 text-gray-700" for="label">
                                                                    {{ $t('Search') }}
                                                                </label>
                                                                <div class="relative flex-grow focus-within:z-10">
                                                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                                                    </div>
                                                                    <input
                                                                        id="search"
                                                                        v-model.lazy="filters.search"
                                                                        :placeholder="$t('Search')"
                                                                        aria-label="Search"
                                                                        class="form-input block w-full rounded-l-md pl-10 text-sm transition ease-in-out duration-150"
                                                                        @change="getCustomer"
                                                                    >
                                                                </div>
                                                            </div>
                                                            <div class="col-span-3 mb-2">
                                                                <label class="block text-sm font-medium leading-5 text-gray-700" for="label">
                                                                    {{ $t('Neighborhood') }}
                                                                </label>
                                                                <div class="mt-1 relative rounded-md shadow-sm">
                                                                    <input-select
                                                                        id="neighborhood"
                                                                        v-model='filters.neighborhood'
                                                                        :options="neighborhoodList"
                                                                        option-label="name"
                                                                        @change="getCustomer"
                                                                        searchable
                                                                    >
                                                                        <!-- Plantilla para la opción seleccionada -->
                                                                        <template v-slot:selectedOption="props">
                                                                            <template v-if="props && !props.anySelected">
                                                                                <span class="block truncate">{{ $t('Select an option') }}</span>
                                                                            </template>
                                                                            <template v-else>
                                                                                <span class="block truncate">{{ props.option.name }}</span>
                                                                            </template>
                                                                        </template>
                            
                                                                        <!-- Plantilla para cada opción en la lista -->
                                                                        <template v-slot:selectOption="props">
                                                                            <div :class="props.option.id === filters.neighborhood ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                                                                {{ props.option.name }}
                                                                            </div>
                                                                        </template>
                                                                    </input-select>
                                                                </div>
                                                            </div>
                                                            <div class="col-span-3 mb-2">
                                                                <label class="block text-sm font-medium leading-5 text-gray-700" for="label">
                                                                    {{ $t('Items per page') }}
                                                                </label>
                                                                <input-select
                                                                    id="per_page"
                                                                    v-model="perPage"
                                                                    :options="[{id: 5, name: 5}, {id: 10, name: 10}, {id: 25, name: 25}, {id: 50, name: 50}]"
                                                                    option-label="name"
                                                                    required
                                                                    @change="getCustomer"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </transition>
                                </section>
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        class="rounded-md border border-gray-400 px-3 py-2 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150"
                        @click="getCustomer"
                    >
                        <svg-vue class="w-4 h-4" icon="font-awesome.sync-regular"></svg-vue>
                    </button>
                </div>
            </div>
        </div>
        <loading :status="loading"/>
        <div class="tickets-list">
            <template v-if="customerList.length > 0">
                <div class="sm:hidden">
                    <ul class="border-b border-gray-200 divide-y divide-gray-200">
                        <template v-for="ticket in customerList">
                            <li>
                                <router-link
                                    :to="'/dashboard/admin/customers/' + ticket.id + '/edit'"
                                    class="flex items-center justify-between px-4 py-4 hover:bg-gray-100 sm:px-6"
                                >
                                    <div class="flex items-center truncate space-x-3">
                                        <div class="text-sm">
                                            {{ ticket.name }}
                                        </div>
                                    </div>
                                    <svg-vue class="ml-4 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-600 transition ease-in-out duration-150" icon="font-awesome.angle-right-regular"></svg-vue>
                                </router-link>
                            </li>
                        </template>
                    </ul>
                </div>
                <div class="hidden sm:block">
                    <div class="align-middle inline-block min-w-full border-b border-gray-200 pl-4">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                            <tr>
                                <th class="px-3 py-2 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                                    #
                                </th>
                                <th class="px-3 py-2 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                                    {{ $t('Name') }}
                                </th>
                                <th class="px-3 py-2 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                                    {{ $t('Phone') }}
                                </th>
                                <th class="px-3 py-2 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                                    {{ $t('Neighborhood') }}
                                </th>
                            </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-100">
                            <template v-for="ticket in customerList">
                                <router-link
                                    :to="'/dashboard/admin/customers/' + ticket.id + '/edit'"
                                    class="cursor-pointer hover:bg-gray-100"
                                    tag="tr"
                                >
                                    <td class="px-3 py-4 whitespace-no-wrap leading-5">
                                        <div class="text-sm">
                                            {{ ticket.id }}
                                        </div>
                                    </td>
                                    <td class="px-3 py-4 whitespace-no-wrap leading-5">
                                        <div class="text-sm">
                                            {{ ticket.name }}
                                        </div>
                                    </td>
                                    <td class="px-3 py-4 whitespace-no-wrap leading-5">
                                        <div class="text-sm">
                                            {{ ticket.phone }}
                                        </div>
                                    </td>
                                    <td class="px-3 py-4 whitespace-no-wrap leading-5">
                                        <p class="text-sm">
                                            {{ ticket.neighborhood ? ticket.neighborhood.name : '' }}
                                        </p>
                                    </td>
                                </router-link>
                            </template>
                            </tbody>
                        </table>
                    </div>
                </div>
            </template>
            <template v-else-if="!loading">
                <div class="h-full flex">
                    <div class="m-auto">
                        <div class="grid grid-cols-1 justify-items-center h-full w-full p-4">
                            <div class="flex justify-center items-center">
                                <svg-vue class="h-full h-auto w-48 mb-6" icon="undraw.task-list"></svg-vue>
                            </div>
                            <div class="flex justify-center items-center">
                                <div class="w-full font-semibold text-2xl">{{ $t('No records found') }}</div>
                            </div>
                            <template v-if="anyFilter">
                                <div class="flex justify-center items-center">
                                    <div>{{ $t('Try changing the filters, or rephrasing your search') }}.</div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </template>
        </div>
        <nav class="bg-white absolute bottom-0 left-0 w-full px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div class="hidden sm:block">
                <p class="text-sm leading-5 text-gray-700">
                    {{ $t('Showing') }}
                    <span class="font-medium">{{ (pagination.perPage * pagination.currentPage) - pagination.perPage + 1 }}</span>
                    {{ $t('to') }}
                    <span class="font-medium">{{ pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total }}</span>
                    {{ $t('of') }}
                    <span class="font-medium">{{ pagination.total }}</span>
                    {{ $t('results') }}
                </p>
            </div>
            <div class="flex-1 flex justify-between sm:justify-end">
                <button
                    :class="pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
                    :disabled="pagination.currentPage <= 1"
                    class="pagination-link"
                    type="button"
                    @click="changePage(pagination.currentPage - 1)"
                >
                    {{ $t('Previous') }}
                </button>
                <button
                    :class="pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
                    :disabled="pagination.currentPage >= pagination.totalPages"
                    class="ml-3 pagination-link"
                    type="button"
                    @click="changePage(pagination.currentPage + 1)"
                >
                    {{ $t('Next') }}
                </button>
            </div>
        </nav>
        <div v-show="quickActions.delete" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <transition
                    duration="300"
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div v-show="quickActions.delete" class="fixed inset-0 transition-opacity">
                        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                    </div>
                </transition>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="quickActions.delete"
                        aria-labelledby="modal-headline"
                        aria-modal="true"
                        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                        role="dialog"
                    >
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <svg-vue class="h-6 w-6 pb-1 text-red-600" icon="font-awesome.exclamation-triangle-light"></svg-vue>
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 id="modal-headline" class="text-lg leading-6 font-medium text-gray-900">
                                        {{ $t('Delete tickets') }}
                                    </h3>
                                    <div class="mt-2">
                                        <p class="text-sm leading-5 text-gray-500">
                                            {{ $t('Are you sure you want to delete the selected tickets?') }}
                                            {{ $t('All data will be permanently removed') }}.
                                            {{ $t('All related data will be deleted') }}.
                                            {{ $t('This action cannot be undone') }}.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                class="btn btn-red mr-2 sm:mr-0"
                                type="button"
                                @click="quickAction('delete')"
                            >
                                {{ $t('Delete tickets') }}
                            </button>
                            <button
                                class="btn btn-white mr-0 sm:mr-2"
                                type="button"
                                @click="quickActions.delete = false"
                            >
                                {{ $t('Cancel') }}
                            </button>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
import {mixin as clickaway} from "vue-clickaway";

export default {
    name: "list",
    metaInfo() {
        return {
            title: this.$i18n.t('Tickets')
        }
    },
    mixins: [clickaway],
    data() {
        return {
            loading: true,
            filtersSidebar: false,
            filters: {
                search: '',
                customer: null,
                user: '',
                agents: [],
                departments: [],
                labels: [],
                statuses: [1, 2],
                priorities: [],
                neighborhood: null,
                report_type: null,
            },
            quickActions: {
                agent: false,
                department: false,
                label: false,
                priority: false,
                delete: false,
            },
            sort: {
                order: 'desc',
                column: 'updated_at',
            },
            page: 1,
            perPage: 10,
            pagination: {
                currentPage: 0,
                perPage: 0,
                total: 0,
                totalPages: 0
            },
            agentList: [],
            customerList: [],
            neighborhoodList: [],
            reportTypes: [],
            departmentList: [],
            labelList: [],
            statusList: [],
            priorityList: [],
            ticketList: [],
            selectAll: false,
            selectedRows: [],
        }
    },
    computed: {
        anyFilter() {
            return this.filters.search !== ''
                || this.filters.user !== ''
                || this.filters.agents !== 0
                || this.filters.departments.length !== 0
                || this.filters.labels.length !== 0
                || this.filters.statuses.length !== 0
                || this.filters.priorities.length !== 0;
        }
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    mounted() {
        // this.getTickets();
        this.getCustomer();
        this.getFilters();
    },
    methods: {
        openFiltersSidebar() {
            const self = this;
            self.selectedRows = [];
            self.selectTicket();
            setTimeout(function () {
                self.filtersSidebar = true;
            }, 100);
        },
        closeFiltersSidebar() {
            this.filtersSidebar = false;
        },
        closeQuickActionDropdown() {
            this.quickActions.agent = false;
            this.quickActions.department = false;
            this.quickActions.label = false;
            this.quickActions.priority = false;
            this.quickActions.delete = false;
        },
        toggleQuickActionDropdown(quickAction) {
            if (quickAction === 'delete') {
                this.quickActions.delete = !this.quickActions.delete;
                this.quickActions.agent = false;
                this.quickActions.department = false;
                this.quickActions.label = false;
                this.quickActions.priority = false;
            }
        },
        changePage(page) {
            const self = this;
            if ((page > 0) && (page <= self.pagination.totalPages) && (page !== self.page)) {
                self.page = page;
                self.getCustomer();
            }
        },
        changeSort() {
            const self = this;
            if (self.sort.order === 'asc') {
                self.sort.order = 'desc';
            } else if (self.sort.order === 'desc') {
                self.sort.order = 'asc';
            }
            self.getCustomer();
        },
        selectTicket() {
            this.closeQuickActionDropdown();
            this.selectAll = Object.keys(this.selectedRows).length === Object.keys(this.ticketList).length;
        },
        selectAllTickets() {
            this.selectedRows = [];
            this.closeQuickActionDropdown();
            if (this.selectAll) {
                for (let ticket in this.ticketList) {
                    this.selectedRows.push(this.ticketList[ticket].id);
                }
            }
        },
        getTickets() {
            const self = this;
            self.selectAll = false;
            self.selectedRows = [];
            self.loading = true;
            axios.get('api/dashboard/tickets', {
                params: {
                    page: self.page,
                    sort: self.sort,
                    perPage: self.perPage,
                    search: self.filters.search,
                    user: self.filters.user,
                    agents: self.filters.agents,
                    departments: self.filters.departments,
                    labels: self.filters.labels,
                    statuses: self.filters.statuses,
                    priorities: self.filters.priorities,
                    neighborhood: self.filters.neighborhood,
                    reportType: self.filters.report_type,
                    customer: self.filters.customer
                }
            }).then(function (response) {
                self.ticketList = response.data.items;
                self.pagination = response.data.pagination;
                if (self.pagination.totalPages < self.pagination.currentPage) {
                    self.page = self.pagination.totalPages;
                    self.getTickets();
                } else {
                    if (self.ticketList.length === 0) {
                        self.selectAll = false;
                        self.selectedRows = [];
                    }
                    self.loading = false;
                }
            }).catch(function () {
                self.loading = false;
            });
        },
        getCustomer() {
            const self = this;
            self.selectAll = false;
            self.selectedRows = [];
            self.loading = true;
            axios.get('api/dashboard/admin/customers', {
                params: {
                    page: self.page,
                    sort: self.sort,
                    perPage: self.perPage,
                    search: self.filters.search,
                    neighborhood: self.filters.neighborhood,
                }
            }).then(function (response) {
                self.customerList = response.data.items;
                // self.ticketList = response.data.items;
                self.pagination = response.data.pagination;
                if (self.pagination.totalPages < self.pagination.currentPage) {
                    self.page = self.pagination.totalPages;
                    self.getCustomer();
                } else {
                    if (self.ticketList.length === 0) {
                        self.selectAll = false;
                        self.selectedRows = [];
                    }
                    self.loading = false;
                }
            }).catch(function () {
                self.loading = false;
            });
        },
        getFilters() {
            const self = this;
            axios.get('api/dashboard/tickets/filters').then(function (response) {
                self.agentList = response.data.agents;
                self.departmentList = response.data.departments;
                self.labelList = response.data.labels;
                self.statusList = response.data.statuses;
                self.priorityList = response.data.priorities;
                self.neighborhoodList = response.data.neighborhoodList;
                self.reportTypes = response.data.reportTypes;
                // self.customerList = response.data.clients;
            });
        },
        quickAction(param, value) {
            const self = this;
            axios.post('api/dashboard/tickets/quick-actions', {
                action: param,
                value: value,
                tickets: self.selectedRows,
            }).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: response.data.message,
                    type: 'success'
                });
                self.getTickets();
            }).catch(function () {
                self.closeQuickActionDropdown();
            });
        },
    }
}
</script>

<style lang="scss">
.tickets-list {
    @apply bg-white w-full z-0 overflow-y-auto;
    height: calc(100vh - 270px);
}

@media (min-width: theme('screens.md')) {
    .tickets-list {
        height: calc(100vh - 216px);
    }
}

.filters-options {
    @apply rounded-md bg-white shadow-xs p-3 overflow-y-auto;
    height: calc(100vh - 270px);
    max-height: 360px;
}

@media (min-width: theme('screens.md')) {
    .filters-options {
        height: calc(100vh - 140px)
    }
}

.tickets-list-toolbar {
    @apply bg-white fixed flex rounded-md shadow-lg;
    left: 50px;
}

@media (min-width: theme('screens.md')) {
    .tickets-list-toolbar {
        left: 50%;
    }
}
</style>
